
import { defineComponent, PropType, ref, watch } from 'vue'

import MultiTranslatedTextField from './MultiTranslatedTextField.vue'
import MultiTranslatedMediaPicker from './MultiTranslatedMediaPicker.vue'
import { useComponentEntries } from '@/composables/use-component-entries'

interface FaqComponentEntryData {
  name: 'faq_entry'
  question: string
  answer: string
  image: string | null
}

interface FaqComponentData {
  name: 'faq'
  entries: FaqComponentEntryData[]
}

export default defineComponent({
  name: 'FaqComponent',
  components: {
    MultiTranslatedTextField,
    MultiTranslatedMediaPicker
  },

  props: {
    initialData: {
      type: Object as PropType<FaqComponentData | undefined>,
      default: undefined
    }
  },

  emits: ['change'],

  setup (props, { expose, emit }) {
    const data = ref<FaqComponentData>({
      name: 'faq',
      entries: []
    })

    watch(() => props.initialData, (newData) => {
      if (newData) {
        data.value = { ...newData, entries: [...newData.entries] }
      }
    }, { immediate: true })

    const { removeEntry: removeEntryAndReindex, nextKeyPrefix } = useComponentEntries<FaqComponentEntryData>(
      () => data.value.entries,
      {
        translationKeyFields: ['question', 'answer', 'image'],
        computeKeyPrefix: (_entries, index) => `faq.entries.[${index + 1}]`
      }
    )

    const addEntry = () => {
      const prefix = nextKeyPrefix()
      data.value.entries.push({
        name: 'faq_entry',
        question: prefix + '.question',
        answer: prefix + '.answer',
        image: prefix + '.image'
      })
      emit('change')
    }

    const removeEntry = (index: number) => {
      removeEntryAndReindex(index)
      emit('change')
    }

    const getComponentData = () => data.value

    expose({ getComponentData })

    return {
      data,
      addEntry,
      removeEntry
    }
  }
})
